<template>
  <ListingBlockLayout
    :class="`listing-block--${view.cssClassModifier}`"
    :title="view.pageTitle()"
    :viewAllLink="view.viewAllLink"
    :viewAllText="view.viewAllText"
    :viewAllQuery="view.viewAllQuery"
    :isEmpty="docs.length == 0"
  >
    <template v-if="$slots.headerActions" #headerActions>
      <slot name="headerActions"></slot>
    </template>
    <ListCard
      v-for="doc in docs"
      :key="doc.id"
      :view="view"
      :doc="doc"
      v-bind="cardProps"
      @click="trackDocView($event, doc)"
    />
  </ListingBlockLayout>
</template>

<script setup lang="ts">
import { inject, onMounted } from 'vue'
import { useRouter } from 'vue-router'

import { Mixpanel } from '@/analytics/mixpanel'
import type { ListView } from '@/models/docs.list'
import type { ListItem } from '@/models/interfaces'
import { Sentry } from '@/services/sentry'
import { playerSession } from '@/services/player'

import ListingBlockLayout from './ListingBlockLayout.vue'
import ListCard from './ListCard.vue'

const props = defineProps<{
  view: ListView
  docs: ListItem[]
  cardProps?: Record<string, any>
  docViewEventProperties?: (doc: ListItem) => Record<string, unknown>
}>()

const router = useRouter()

// Discover.vue provides this callback, so when in Discover the ListingBlocks
// call it and register the docs in their list.
const registerDiscoverListingBlock = inject<
  ((title: string, docTitles: string[]) => void) | undefined
>('registerDiscoverListingBlock', undefined)

onMounted(() => {
  registerDiscoverListingBlock?.(
    props.view.pageTitle(),
    props.docs.map((doc) => doc.title),
  )
})

function trackDocView(event: Event, doc: ListItem): void {
  try {
    playerSession.value.setNextArrival('card')
    Mixpanel.trackDocView(
      event,
      props.view.pageTitle(),
      router.currentRoute.value.fullPath,
      doc,
      props.docViewEventProperties?.(doc),
    )
  } catch (error: any) {
    // Unexpected error in the Mixpanel tracking
    Sentry.captureException(error)
  }
}
</script>
